// src/utils/auth.ts
import { UserRole, AuthUser } from '@/types/models';
import { getImpersonation, stopImpersonation } from './impersonation';

/**
 * Obtiene el token del localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Guarda el token en localStorage
 */
export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
};

/**
 * Elimina el token del localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem('token');
};

/**
 * Verifica si el usuario está autenticado
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;
  
  // Verificar si el token no ha expirado
  const expiration = localStorage.getItem('token_expiration');
  if (expiration) {
    const expirationTime = parseInt(expiration, 10);
    if (Date.now() > expirationTime) {
      // Token expirado, limpiar
      clearAuthData();
      return false;
    }
  }
  
  return true;
};

/**
 * Obtiene el rol actual del usuario. Mientras se está suplantando a otro usuario (ver
 * utils/impersonation.ts) este es el rol del OBJETIVO — toda decisión basada en el rol
 * (menús, guards, permisos) debe seguir el perfil suplantado, igual que hace el API en cada
 * petición. El rol real permanece intacto en localStorage y vuelve en cuanto termina la
 * suplantación.
 */
export const getCurrentRole = (): UserRole | null => {
  const impersonation = getImpersonation();
  if (impersonation) return impersonation.role;
  const role = localStorage.getItem('role');
  return role as UserRole | null;
};

/**
 * Obtiene el ID del usuario actual
 */
export const getCurrentUserId = (): string | null => {
  return localStorage.getItem('userId');
};

/**
 * Obtiene el nombre del usuario actual
 */
export const getCurrentUserName = (): string | null => {
  return localStorage.getItem('username');
};

/**
 * Obtiene el email del usuario actual
 */
export const getCurrentUserEmail = (): string | null => {
  return localStorage.getItem('email');
};

/**
 * Obtiene el hotel actual del usuario
 */
export const getCurrentHotel = (): string | null => {
  return localStorage.getItem('hotelId');
};

/**
 * Obtiene el grupo actual del usuario
 */
export const getCurrentGroup = (): string | null => {
  return localStorage.getItem('groupId');
};

/**
 * Guarda los datos de autenticación
 */
export const saveAuthData = (data: {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    hotelId?: string;
    groupId?: string;
  };
  expiresIn?: number;
}): void => {
  const { token, user, expiresIn } = data;
  
  // Guardar token
  setToken(token);
  
  // Guardar datos del usuario
  localStorage.setItem('userId', user.id);
  localStorage.setItem('username', user.name);
  localStorage.setItem('email', user.email);
  localStorage.setItem('role', user.role);
  
  if (user.hotelId) {
    localStorage.setItem('hotelId', user.hotelId);
  }
  
  if (user.groupId) {
    localStorage.setItem('groupId', user.groupId);
  }
  
  // Guardar expiración si existe
  if (expiresIn) {
    const expiration = Date.now() + (expiresIn * 1000);
    localStorage.setItem('token_expiration', expiration.toString());
  }
};

/**
 * Limpia todos los datos de autenticación
 */
export const clearAuthData = (): void => {
  stopImpersonation();
  localStorage.removeItem('token');
  localStorage.removeItem('token_expiration');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('email');
  localStorage.removeItem('role');
  localStorage.removeItem('hotelId');
  localStorage.removeItem('groupId');
};

/**
 * Obtiene todos los datos del usuario actual
 */
export const getCurrentUser = (): Partial<AuthUser> | null => {
  if (!isAuthenticated()) return null;
  
  return {
    id: getCurrentUserId() || undefined,
    name: getCurrentUserName() || undefined,
    email: getCurrentUserEmail() || undefined,
    role: getCurrentRole() || undefined,
    hotelId: getCurrentHotel() || undefined,
    groupId: getCurrentGroup() || undefined,
    token: getToken() || undefined,
  };
};


export    const decodeJWT = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};


/**
 * Verifica si un token ha expirado
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    return true; // Si no podemos decodificar o no tiene exp, consideramos que expiró
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

/**
 * Obtiene el tiempo restante hasta que expire el token (en segundos)
 */
export const getTokenTimeRemaining = (token: string): number => {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    return 0;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = decoded.exp - currentTime;
  
  return Math.max(0, timeRemaining);
};

/**
 * Obtiene la fecha de expiración del token
 */
export const getTokenExpirationDate = (token: string): Date | null => {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  return new Date(decoded.exp * 1000);
};

/**
 * Extrae información adicional del token JWT
 */
export const extractTokenData = (token: string): {
  hotelId?: string;
  groupId?: string;
  hotelName?: string;
  groupName?: string;
  exp?: number;
} | null => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  return {
    hotelId: decoded.hotelId,
    groupId: decoded.groupId,
    hotelName: decoded.hotelName,
    groupName: decoded.groupName,
    exp: decoded.exp,
  };
};

/**
 * Verifica si el usuario tiene acceso a nivel de hotel
 */
export const hasHotelAccess = (): boolean => {
  const role = getCurrentRole();
  return role === 'HotelAdmin' || role === 'Receptionist' || role === 'Developer';
};

/**
 * Verifica si el usuario tiene acceso a nivel de grupo
 */
export const hasGroupAccess = (): boolean => {
  const role = getCurrentRole();
  return role === 'GroupAdmin';
};

/**
 * Verifica si el usuario tiene acceso a nivel de sistema
 */
export const hasSystemAccess = (): boolean => {
  const role = getCurrentRole();
  return role === 'SuperAdmin';
};


const roleLevels: Record<string, number> = {
    'SuperAdmin': 0,
    'GroupAdmin': 1,
    'HotelAdmin': 2,
    'Receptionist': 3,
    'Developer': 4,
};

export const getCurrentLevel = () => {
    const role = getCurrentRole();
    return role ? roleLevels[role] ?? 4 : 4;
};